import { useController, type FieldPathByValue, type FieldValues, type UseControllerProps } from 'react-hook-form';
import { PillRemovable } from '#components/atoms/pill/PillRemovable.tsx';
import { SelectAsyncSearchable } from '#components/patterns/forms/select/select-async-searchable/SelectAsyncSearchable.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { allTagsQueryOptions } from './SelectTagsFilter.queries.ts';

type Props<T extends FieldValues, N extends FieldPathByValue<T, string[]>> = UseControllerProps<T, N> & {
  label: React.ReactNode;
  className?: string;
};

export function SelectTagsFilter<T extends FieldValues, N extends FieldPathByValue<T, string[]>>({
  label,
  className,
  control,
  name,
}: Props<T, N>) {
  const {
    field: { value: tags, onChange },
  } = useController({ name, control });

  function addTag(tag: string) {
    if (!tags.includes(tag)) {
      onChange([...tags, tag]);
    }
  }

  function removeTag(tag: string) {
    onChange(tags.filter((t: string) => t !== tag));
  }

  return (
    <SelectAsyncSearchable
      getItemsQueryOptions={allTagsQueryOptions}
      label={label}
      placeholder="Choose tags…"
      optionsName="tags"
      className={className}
      fieldChildren={
        tags.length > 0 ?
          <div className="flex flex-wrap gap-4 mt-8">
            {tags.map((tag: string) => (
              <PillRemovable key={tag} onRemove={() => removeTag(tag)}>
                {tag}
              </PillRemovable>
            ))}
          </div>
        : null
      }
    >
      {(items) =>
        items
          .filter((item) => !tags.includes(item.value))
          .map((item) => (
            <SelectItem
              value={item.value}
              onClick={() => addTag(item.value)}
              setValueOnClick={false}
              searchable={true}
              key={item.value}
            >
              {item.label}
            </SelectItem>
          ))
      }
    </SelectAsyncSearchable>
  );
}
